import { Feature, Map as OlMap } from "ol";
import WKT from "ol/format/WKT";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { Stroke, Style } from "ol/style";
import { SitePolygoneDto } from "src/app/bo/site/sitePolygone";
import { DxfServiceService } from "src/app/services/dxf-service.service";
import MapConstants from "../constants";


export class SitePolygonLayer{

    private vectorLayers: VectorLayer<VectorSource>[] = [];
    /** Layers indexed by polygone id, so each DXF can be toggled individually */
    private readonly layersByPolygoneId = new Map<number, VectorLayer<VectorSource>>();
    private polygones: SitePolygoneDto[] = [];
    private epsg = 4326; // Default EPSG
    private readonly zIndex: number;

    public constructor(private readonly dxfService: DxfServiceService, private readonly map:OlMap, private siteId: number, zIndex:number, epsg?: number){
        this.zIndex = zIndex;

        if (epsg) {
            this.epsg = epsg;
        }
    }

    public changeSite(siteId: number, epsg?: number): Promise<SitePolygoneDto[]> {
        this.clearLayers();
        this.siteId = siteId;

        if (epsg) {
            this.epsg = epsg;
        }

        return this.dxfService.loadSitepolygones(this.siteId).then(polygones => {
            this.polygones = polygones ?? [];
            this.polygones.forEach(polygone => {
                this.addPolygone(polygone);
            });
            return this.polygones;
        });
    }

    /** DXF layers of the current site, for the visibility panel */
    public getPolygones(): SitePolygoneDto[] {
        return this.polygones;
    }

    /** Shows or hides a single DXF layer on the map */
    public setPolygoneVisibility(polygoneId: number, isVisible: boolean) {
        this.layersByPolygoneId.get(polygoneId)?.setVisible(isVisible);

        const polygone = this.polygones.find(p => p.id === polygoneId);
        if (polygone) {
            polygone.isVisible = isVisible;
        }
    }

    private clearLayers() {
        // Remove all layers from the map
        this.vectorLayers.forEach(layer => {
            this.map.removeLayer(layer);
        });
        this.vectorLayers = [];
        this.layersByPolygoneId.clear();
        this.polygones = [];
    }

    private addPolygone(polygoneDto: SitePolygoneDto){
        let layer: VectorLayer<VectorSource> | null = null;

        // Check if we have compressed DXF data (new format)
        if (polygoneDto.compressedDxf) {
            try {
                // Decompress the DXF content
                const dxfContent = this.dxfService.decompressDxf(polygoneDto.compressedDxf);
                
                // Parse the DXF file
                const entities = this.dxfService.parseDxfFile(dxfContent);
                
                if (entities && entities.length > 0) {
                    // Create vector layer with DXF colors using the site's EPSG
                    layer = this.dxfService.createVectorLayer(entities, this.epsg);
                    const dxfSource = layer.getSource();
                    
                    if (dxfSource) {
                        // Set metadata only
                        dxfSource.getFeatures().forEach(feature => {
                            feature.set(MapConstants.FEATURE_NAME, MapConstants.FEATURE_TYPE.site_polygone);
                        });
                    }
                }
            } catch (err) {
                console.error('Error loading DXF polygon:', err);
                // Fall back to WKT if DXF fails
                layer = this.createWktLayer(polygoneDto);
            }
        } 
        // Legacy format: WKT string
        else if (polygoneDto.wktString) {
            layer = this.createWktLayer(polygoneDto);
        }

        // Add the layer to map if created successfully
        if (layer) {
            layer.setZIndex(this.zIndex);
            // Default to visible when the flag is absent (legacy rows)
            layer.setVisible(polygoneDto.isVisible !== false);
            this.map.addLayer(layer);
            this.vectorLayers.push(layer);
            this.layersByPolygoneId.set(polygoneDto.id, layer);
        }
    }

    private createWktLayer(polygoneDto: SitePolygoneDto): VectorLayer<VectorSource> {
        const source = new VectorSource();
        const wkt = new WKT();
        const wktElements = polygoneDto.wktString!.split('\n');
        
        wktElements.forEach(element => {
            const feature = wkt.readFeature(element, {
                dataProjection: 'EPSG:3857',
                featureProjection: 'EPSG:3857'
            });

            feature.set(MapConstants.FEATURE_NAME, MapConstants.FEATURE_TYPE.site_polygone);

            feature.setStyle(new Style({
                stroke: new Stroke({
                    color: polygoneDto.color,
                    width: 5
                })
            }));

            source.addFeature(feature);
        });

        return new VectorLayer({
            source: source
        });
    }
}